import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { MaintenancesApi } from '@api/maintenances/maintenances.api';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import moment, { isMoment } from 'moment';
import { DialogStopSchedulingSuggestionsComponent } from '../../dialogs/dialog-edit-maintenance/tabs/maintenance/dialog-stop-scheduling-suggestions/dialog-stop-scheduling-suggestions.component';
import { DialogSchedulingSuggestionsComponent } from '../../dialogs/dialog-scheduling-suggestions/dialog-scheduling-suggestions.component';
import { ConfirmDeleteNewSchedulingSuggestionsComponent } from './confirm-delete-new-scheduling-suggestions/confirm-delete-new-scheduling-suggestions.component';

@Component({
  selector: 'schedule-suggestions',
  templateUrl: './schedule-suggestions.component.html',
  styleUrls: ['./schedule-suggestions.component.scss']
})
export class ScheduleSuggestionsComponent implements OnInit {

  @Input()
  public form: FormGroup;

  @Output()
  public isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  public maintenanceId: string;

  public isRetroactiveDate: boolean;
  public scheduleSuggestionsFormArray: FormArray;
  public newScheduleSuggestionFormArray: FormArray;
  public companyScheduleSuggestionIsRetroactiveDate: boolean;
  public corporateScheduleSuggestionsHasRetroactiveDate: boolean;
  public shouldShowButtonsOnOngoingScheduleSuggestions: boolean;
  public sheduleSuggestionsContainersToShow = {
    addSuggestion: false,
    ongoingSuggestions: false,
    corporateAccepted: false,
    scheduleSuggestions: false
  };

  constructor(
    public readonly matDialog: MatDialog,
    private readonly maintenancesApi: MaintenancesApi,
    private readonly customMessageService: CustomMessagesService
  ) { }

  ngOnInit(): void {
    this.scheduleSuggestionsFormArray = this.form.get('scheduleSuggestions') as FormArray;
    this.companyScheduleSuggestionIsRetroactiveDate = this.checkIfCompanyScheduleSuggestionIsRetroactiveDate();
    this.shouldShowButtonsOnOngoingScheduleSuggestions = this.shouldShowButtons();
    this.validateRetroativeDateOnSheduleSuggestions();
    this.updateScheduleSuggestionsContainersToShow();
  }

  updateScheduleSuggestionsContainersToShow(): void {
    const statusForm = this.form.get('status');
    const scheduleSuggestionsStatusForm = this.form.get('scheduleSuggestionsStatus');
    const hasScheduleSuggestions = this.scheduleSuggestionsFormArray.value && this.scheduleSuggestionsFormArray.value.length >= 1;
    const isOnReallocationDialog = this.form.get('newCompany');
    const isOnReopenDialog = this.form.get('reopenReason');
    const hasCompanyScheduleSuggestion = this.form.get('companyScheduleSuggestion') && this.form.get('companyScheduleSuggestion').value;
    const isNewOrRequestedMaintenance = (!hasScheduleSuggestions && !hasCompanyScheduleSuggestion && !statusForm) || (statusForm && (statusForm.value === 'new' || statusForm.value === 'requested'));
    const isOnNegotiation = statusForm && statusForm.value === 'onNegotiation';
    const isCanceled = statusForm && statusForm.value === 'canceled';
    const isStandardSuggested = scheduleSuggestionsStatusForm && scheduleSuggestionsStatusForm.value === 'standardSuggested';
    const isSuggestionStopped = scheduleSuggestionsStatusForm && scheduleSuggestionsStatusForm.value === 'suggestionStopped';
    const isCorporateAccepted = scheduleSuggestionsStatusForm && scheduleSuggestionsStatusForm.value === 'corporateAccepted';
    const isNewScheduleSuggestion = scheduleSuggestionsStatusForm && scheduleSuggestionsStatusForm.value === 'newScheduleSuggestion';

    this.sheduleSuggestionsContainersToShow.addSuggestion = false;
    this.sheduleSuggestionsContainersToShow.ongoingSuggestions = false;
    this.sheduleSuggestionsContainersToShow.corporateAccepted = false;
    this.sheduleSuggestionsContainersToShow.scheduleSuggestions = false;

    if (
      !isNewScheduleSuggestion &&
      (Boolean(isNewOrRequestedMaintenance && !hasScheduleSuggestions) ||
        Boolean(!hasScheduleSuggestions && (isOnReallocationDialog || isOnReopenDialog)) ||
        Boolean(isSuggestionStopped && (isOnReallocationDialog || isOnReopenDialog)) ||
        Boolean(isOnNegotiation && isStandardSuggested && (isOnReallocationDialog || isOnReopenDialog)) ||
        Boolean(isCorporateAccepted && (isOnReallocationDialog || isOnReopenDialog)) ||
        Boolean(isCanceled && (isOnReallocationDialog || isOnReopenDialog) && !hasScheduleSuggestions))
    ) {
      this.sheduleSuggestionsContainersToShow.addSuggestion = true;
    } else if (
      (this.scheduleSuggestionsFormArray &&
        this.scheduleSuggestionsFormArray.value &&
        this.scheduleSuggestionsFormArray.value.length > 0) &&
      ((!isStandardSuggested || isCanceled) && !isCorporateAccepted && !isSuggestionStopped)
    ) {
      this.sheduleSuggestionsContainersToShow.ongoingSuggestions = true;
    } else if (isCorporateAccepted && !isOnReallocationDialog) {
      this.sheduleSuggestionsContainersToShow.corporateAccepted = true;
    } else if (isOnNegotiation && isStandardSuggested) {
      this.sheduleSuggestionsContainersToShow.scheduleSuggestions = true;
    }
  }

  openSchedulingSuggestionsDialog(): void {
    const schedulingSuggestionDialog = this.matDialog.open(DialogSchedulingSuggestionsComponent, {
      panelClass: ['scheduling-suggestions'],
      width: '553px',
      disableClose: true,
      data: this.form
    });

    schedulingSuggestionDialog.afterClosed().subscribe(() => {
      this.updateScheduleSuggestionsContainersToShow();
      this.validateRetroativeDateOnSheduleSuggestions();
    });
  }

  openConfirmDeleteNewSchedulingSuggestionsDialog(): void {
    const confirmDeleteDialog = this.matDialog.open(ConfirmDeleteNewSchedulingSuggestionsComponent, {
      panelClass: ['confirm-delete-new-scheduling-suggestions'],
      width: '464px',
      disableClose: true,
      data: this.form
    });

    confirmDeleteDialog.afterClosed().subscribe(() => {
      this.updateScheduleSuggestionsContainersToShow();
    });
  }

  openStopSchedulingSuggestionsDialog(): void {
    this.matDialog.open(DialogStopSchedulingSuggestionsComponent, {
      panelClass: ['stop-scheduling-suggestions'],
      width: '464px',
      data: this.form
    }).componentInstance.isLoading.subscribe((reload) => {
      this.isLoading.next(reload);
    });
  }

  validateRetroativeDateOnSheduleSuggestions(): void {
    this.corporateScheduleSuggestionsHasRetroactiveDate = this.checkIfCorporateScheduleSuggestionsHasRetroactiveDate();
    if (this.corporateScheduleSuggestionsHasRetroactiveDate) {
      this.form.get('scheduleSuggestions').setErrors({
        invalid: true
      });
    }
  }

  checkIfCompanyScheduleSuggestionIsRetroactiveDate(): boolean {
    if (!this.form.get('companyScheduleSuggestion')) {
      return false;
    }

    if (!this.form.get('scheduleSuggestionsStatus')) {
      return false;
    }

    if (this.form.get('scheduleSuggestionsStatus').value !== 'standardSuggested') {
      return false;
    }

    if (!this.form.get('companyScheduleSuggestion').value) {
      return false;
    }

    const companyScheduleSuggestion = this.form.get('companyScheduleSuggestion').value;
    const currentDate = moment();
    const selectedDateTime = moment(`${companyScheduleSuggestion.date} ${companyScheduleSuggestion.time}`, 'YYYY-MM-DD HH:mm');
    const isDateToday = companyScheduleSuggestion.date === moment(currentDate).format('YYYY-MM-DD');

    if (isDateToday && !companyScheduleSuggestion.time) {
      return false;
    }

    if (selectedDateTime.isBefore(currentDate)) {
      return true;
    }

    return false;
  }

  checkIfCorporateScheduleSuggestionsHasRetroactiveDate(): boolean {
    if (!this.scheduleSuggestionsFormArray.value || !this.scheduleSuggestionsFormArray.value.length) {
      return false;
    }

    const currentDate = moment();

    return this.scheduleSuggestionsFormArray.value.some((scheduleSuggestion) => {
      if (isMoment(scheduleSuggestion.date)) {
        scheduleSuggestion.date = moment(scheduleSuggestion.date).format('YYYY-MM-DD');
      }

      const selectedDateTime = moment(`${scheduleSuggestion.date} ${scheduleSuggestion.time}`, 'YYYY-MM-DD HH:mm');
      const isDateToday = scheduleSuggestion.date === currentDate.format('YYYY-MM-DD');

      if (isDateToday && !scheduleSuggestion.time) {
        return false;
      }

      return selectedDateTime.isBefore(currentDate);
    });
  }

  shouldShowButtons(): boolean {
    const scheduleSuggestionsStatus = this.form.get('scheduleSuggestionsStatus');
    const isOnReallocationDialog = this.form.get('newCompany');
    const maintenanceStatus = this.form.get('status');
    const isOnReopenDialog = this.form.get('reopenReason');
    const isMaintenanceRequested = Boolean(maintenanceStatus && maintenanceStatus.value === 'requested');

    return Boolean(
      isMaintenanceRequested || !maintenanceStatus ||
      ((maintenanceStatus && maintenanceStatus.value !== 'canceled' || (isOnReallocationDialog || isOnReopenDialog)) &&
        ((isOnReallocationDialog || isOnReopenDialog) ||
          ((scheduleSuggestionsStatus && scheduleSuggestionsStatus.value !== 'corporateSuggested' &&
            (scheduleSuggestionsStatus.value !== 'corporateRefused')) ||
            scheduleSuggestionsStatus.value === 'newScheduleSuggestion' ||
            (maintenanceStatus && maintenanceStatus.value === 'new' &&
              scheduleSuggestionsStatus && scheduleSuggestionsStatus.value === 'corporateSuggested'))))
    );
  }

  async acceptMaintenanceScheduleSuggestions(): Promise<void> {
    try {
      this.isLoading.emit(true);
      await this.maintenancesApi.acceptMaintenanceScheduleSuggestions(this.maintenanceId).toPromise();
      const maintenance = await this.maintenancesApi.searchOne(this.maintenanceId).toPromise();
      this.form.get('status').setValue(maintenance.status);
      this.form.get('scheduleSuggestionsStatus').setValue(maintenance.scheduleSuggestionsStatus);

      this.customMessageService.success('MAINTENANCES.SCHEDULE_SUGGESTIONS.ACCEPT_MAINTENANCE_SCHEDULE_SUGGESTIONS.SUCCESS');
    } catch (err) {
      this.customMessageService.error('MAINTENANCES.SCHEDULE_SUGGESTIONS.ACCEPT_MAINTENANCE_SCHEDULE_SUGGESTIONS.ERROR');
    } finally {
      this.updateScheduleSuggestionsContainersToShow();
      this.isLoading.emit(false);
    }
  }
}
